/**
 * @fileoverview Snippets Settings — Variant Snippet Library
 *
 * Reusable wording snippets for variant blocks. Each snippet belongs to a
 * (variantGroup, optionValue) pair and has a name + HTML body. The body is
 * authored in the full TipTap editor so users can apply formatting and
 * insert canonical/custom field chips that get filled in at job-generation
 * time, just like they would inside a template.
 *
 * URL: /dashboard/settings/snippets
 */

import { createFileRoute } from "@tanstack/react-router";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "~/convex/_generated/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useActiveOrg } from "@/utils/org-context";
import { Badge, Button, Input, Spinner } from "@/ui/primitives";
import { useCallback, useMemo, useState } from "react";
import {
  Library,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  ChevronDown,
  ChevronRight,
  X,
  Info,
} from "lucide-react";
import DOMPurify from "dompurify";
import {
  prepareHtmlForEditor,
  buildFieldsLabelMap,
  decorateFieldChipModifiers,
  stripInlineHighlights,
} from "@/ui/composed/editor/html-utils";
import { cn } from "@/utils/misc";
import type { Id } from "~/convex/_generated/dataModel";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/composed/overlays";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { TemplateEditor } from "@/ui/composed/editor/template-editor";
import {
  FIELD_CATEGORIES,
  type CanonicalField,
  type FieldType,
} from "~/convex/settings/precedents/canonicalFields";
import type { VariantFieldBinding } from "~/convex/jobs/precedents/variantBinding";
import {
  CanonicalKeyPicker,
  type CustomFieldDef,
} from "@/components/settings/precedents/CanonicalKeyPicker";
import {
  classifyFieldSource,
  type FieldSource,
} from "@/utils/precedents/classifyFieldSource";
import { CONTACT_ROLE_KINDS, FIRM_CATEGORY_ORDER } from "@/lib/contact-roles";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import type {
  TemplateEditorField,
  EditorCustomField,
  CreateCustomFieldInput,
} from "@/ui/composed/editor/template-editor";
import { extractErrorMessage } from "@/utils/errors";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/snippets",
)({
  component: SnippetsSettings,
  beforeLoad: () => ({
    title: "Snippets",
    headerTitle: "Settings",
  }),
});

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

interface VariantGroup {
  _id: Id<"variantGroups">;
  name: string;
  slug: string;
  description?: string;
  options: Array<{ value: string; label: string }>;
  /** Optional data trigger — see `variantGroups.fieldBinding` (MOR-886). */
  fieldBinding?: VariantFieldBinding | null;
}

interface VariantSnippet {
  _id: Id<"variantSnippets">;
  name: string;
  contentHtml: string;
  groupSlug: string;
  optionValue: string;
  slotLabel?: string | null;
}

/** Stable empty set — keeps `classifySource` from re-creating each render. */
const EMPTY_KEY_SET: ReadonlySet<string> = new Set<string>();

function SnippetsSettings() {
  const { orgId } = useActiveOrg();
  const typedOrgId = orgId as Id<"organizations">;

  const { data: groups, isLoading: groupsLoading } = useQuery({
    ...convexQuery(api.settings.precedents.variantGroups.listVariantGroups, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  const { data: snippets } = useQuery({
    ...convexQuery(api.settings.precedents.variantSnippets.listSnippets, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  // Org-wide custom fields and presets — feed the data trigger field picker so it
  // offers the same keys, grouping and source colours as Insert Field does
  // in the template editor (MOR-886).
  const { data: customFieldDefs } = useQuery({
    ...convexQuery(api.settings.precedents.customFields.listByOrg, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  const { data: orgPresets } = useQuery({
    ...convexQuery(api.settings.precedents.presets.listByOrg, {
      orgId: typedOrgId,
    }),
    enabled: !!orgId,
  });

  const bindingCustomFields = useMemo<CustomFieldDef[]>(
    () =>
      (customFieldDefs ?? []).map((d) => ({
        _id: d._id,
        categoryId: d.categoryId,
        categoryLabel: d.categoryLabel,
        key: d.key,
        label: d.label,
        fieldType: d.fieldType,
        templateId: d.templateId,
      })),
    [customFieldDefs],
  );

  const customFieldKeys = useMemo(
    () => new Set<string>((customFieldDefs ?? []).map((d) => d.key)),
    [customFieldDefs],
  );

  const orgPresetKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const preset of (orgPresets ?? []) as Array<{
      values?: Record<string, string>;
    }>) {
      for (const k of Object.keys(preset.values ?? {})) keys.add(k);
    }
    return keys;
  }, [orgPresets]);

  // No template context here, so nothing is "from job" by example value.
  const classifySource = useCallback(
    (key: string): FieldSource =>
      classifyFieldSource({
        key,
        customFieldKeys,
        orgPresetKeys,
        templateFieldsWithExample: EMPTY_KEY_SET,
      }),
    [customFieldKeys, orgPresetKeys],
  );

  if (!orgId) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <p className="text-sm text-primary/60">
          Select an organisation to manage variants.
        </p>
      </div>
    );
  }

  if (groupsLoading) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <Spinner className="size-6" />
      </div>
    );
  }

  const groupList = (groups ?? []) as VariantGroup[];
  const allSnippets = (snippets ?? []) as VariantSnippet[];
  const totalSnippets = allSnippets.length;

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Header */}
      <div
        className={`flex items-start justify-between gap-6 p-6 ${GROUP_CARD_CLASS}`}
      >
        <div className="min-w-0 flex-1">
          <h3 className="inline-flex items-center gap-2 text-lg font-semibold text-primary">
            <Library className="size-5 text-primary/50" />
            Variants Library
          </h3>
          <p className="mt-1 text-sm text-primary/60">
            Create and manage blocks of variants to use within precedents (e.g.
            single v joint appointees).
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge variant="secondary">
            {totalSnippets} {totalSnippets === 1 ? "variant" : "variants"}
          </Badge>
        </div>
      </div>

      {/* New variant group card */}
      <NewVariantGroupCard orgId={typedOrgId} />

      {/* Empty state — no variant groups */}
      {groupList.length === 0 && (
        <div className="rounded-xl border border-dashed border-border/40 bg-secondary/20 p-10 text-center">
          <Library className="mx-auto mb-3 size-8 text-primary/20" />
          <p className="text-sm font-medium text-primary/60">
            No variant groups yet
          </p>
          <p className="mt-1 text-xs text-primary/40">
            Create your first variant group above to start building a variant
            library.
          </p>
        </div>
      )}

      {/* One section per variant group */}
      {groupList.map((group) => (
        <GroupSection
          key={group._id}
          orgId={typedOrgId}
          group={group}
          snippets={allSnippets.filter((s) => s.groupSlug === group.slug)}
          bindingCustomFields={bindingCustomFields}
          classifySource={classifySource}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Variant Group Card — create groups directly from this page
// ---------------------------------------------------------------------------

function NewVariantGroupCard({ orgId }: { orgId: Id<"organizations"> }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [optionLabel, setOptionLabel] = useState("");
  const [options, setOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [submitting, setSubmitting] = useState(false);

  const { mutateAsync: createGroup } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.variantGroups.createVariantGroup,
    ),
  });

  const slugify = (s: string) =>
    s
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");

  const addOption = useCallback(() => {
    const label = optionLabel.trim();
    if (!label) return;
    const value = slugify(label);
    if (!value) return;
    if (options.some((o) => o.value === value)) {
      toast.error("Option already exists");
      return;
    }
    setOptions((prev) => [...prev, { value, label }]);
    setOptionLabel("");
  }, [optionLabel, options]);

  const submit = useCallback(async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (options.length === 0) {
      toast.error("Add at least one option");
      return;
    }
    setSubmitting(true);
    try {
      await createGroup({ orgId, name: name.trim(), options });
      toast.success(`Created "${name.trim()}"`);
      setName("");
      setOptions([]);
      setOptionLabel("");
      setOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to create group"));
    } finally {
      setSubmitting(false);
    }
  }, [name, options, orgId, createGroup]);

  const reset = useCallback(() => {
    setOpen(false);
    setName("");
    setOptions([]);
    setOptionLabel("");
  }, []);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          GROUP_CARD_CLASS,
          "flex w-full items-center gap-3 border border-dashed border-border/40 px-6 py-4 text-sm text-primary/50 hover:text-primary/70",
        )}
      >
        <div className="flex size-8 items-center justify-center rounded-lg bg-primary/5">
          <Plus className="size-4" />
        </div>
        New variant group
      </button>
    );
  }

  return (
    <div className={GROUP_CARD_CLASS}>
      <div className="px-6 py-5">
        <div className="mb-4 flex items-center justify-between">
          <h5 className="text-sm font-semibold text-primary">
            New variant group
          </h5>
          <button
            type="button"
            onClick={reset}
            className="rounded-md p-1 text-primary/40 hover:bg-secondary hover:text-primary/70"
            aria-label="Cancel"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="space-y-3">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Group name (e.g. "Practitioner Count")'
            className="h-9 text-sm"
            autoFocus
          />
          <div className="flex flex-wrap gap-1.5">
            {options.map((opt) => (
              <span
                key={opt.value}
                className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-secondary/40 px-2 py-0.5 text-xs"
              >
                {opt.label}
                <button
                  type="button"
                  onClick={() =>
                    setOptions((prev) =>
                      prev.filter((o) => o.value !== opt.value),
                    )
                  }
                  className="text-primary/40 hover:text-primary/70"
                  aria-label={`Remove ${opt.label}`}
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={optionLabel}
              onChange={(e) => setOptionLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addOption();
                }
              }}
              placeholder="Option label (e.g. Single)"
              className="h-9 text-sm"
            />
            <Button size="sm" variant="outline" onClick={addOption}>
              Add option
            </Button>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button
              size="sm"
              onClick={submit}
              disabled={submitting || !name.trim() || options.length === 0}
            >
              {submitting ? (
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
              ) : (
                <Plus className="mr-1.5 size-3.5" />
              )}
              Create group
            </Button>
            <Button size="sm" variant="ghost" onClick={reset}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group Section
// ---------------------------------------------------------------------------

function GroupSection({
  orgId,
  group,
  snippets,
  bindingCustomFields,
  classifySource,
}: {
  orgId: Id<"organizations">;
  group: VariantGroup;
  snippets: VariantSnippet[];
  /** Org custom fields offered by the data trigger field picker. */
  bindingCustomFields: CustomFieldDef[];
  /** Colours the data trigger picker's key chips and powers its source tabs. */
  classifySource: (key: string) => FieldSource;
}) {
  const [expanded, setExpanded] = useState(true);
  const [showAddOption, setShowAddOption] = useState(false);
  const [nameEdit, setNameEdit] = useState(false);
  const [nameDraft, setNameDraft] = useState(group.name);

  const { mutateAsync: updateGroup } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.variantGroups.updateVariantGroup,
    ),
  });

  const commitName = useCallback(async () => {
    setNameEdit(false);
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === group.name) {
      setNameDraft(group.name);
      return;
    }
    try {
      await updateGroup({ groupId: group._id, name: trimmed });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to rename group"));
      setNameDraft(group.name);
    }
  }, [nameDraft, group._id, group.name, updateGroup]);
  const { mutateAsync: deleteGroup } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.variantGroups.deleteVariantGroup,
    ),
  });

  const slugify = (s: string) =>
    s
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");

  const addOption = useCallback(
    async (label: string) => {
      const trimmed = label.trim();
      if (!trimmed) return;
      const value = slugify(trimmed);
      if (!value) return;
      if (group.options.some((o) => o.value === value)) {
        toast.error("Option already exists");
        return;
      }
      try {
        await updateGroup({
          groupId: group._id,
          options: [...group.options, { value, label: trimmed }],
        });
        setShowAddOption(false);
        toast.success(`Added option "${trimmed}"`);
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to add option"));
      }
    },
    [group._id, group.options, updateGroup],
  );

  const { mutateAsync: renameOptionMut } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.variantGroups.renameOption,
    ),
  });

  const renameOption = useCallback(
    async (value: string, nextLabel: string) => {
      const trimmed = nextLabel.trim();
      if (!trimmed) return;
      try {
        await renameOptionMut({
          groupId: group._id,
          oldValue: value,
          newLabel: trimmed,
        });
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to rename option"));
      }
    },
    [group._id, renameOptionMut],
  );

  const deleteOption = useCallback(
    async (value: string) => {
      try {
        await updateGroup({
          groupId: group._id,
          options: group.options.filter((o) => o.value !== value),
        });
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to delete option"));
      }
    },
    [group._id, group.options, updateGroup],
  );

  const handleDeleteGroup = useCallback(async () => {
    try {
      await deleteGroup({ groupId: group._id });
      toast.success(`Deleted "${group.name}"`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to delete group"));
    }
  }, [group._id, group.name, deleteGroup]);

  return (
    <div className={GROUP_CARD_CLASS}>
      {/* Group header */}
      <div className="flex items-center justify-between gap-3 border-b border-border/50 px-6 py-4">
        <div className="flex flex-1 items-center gap-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-left"
            aria-label={expanded ? "Collapse group" : "Expand group"}
          >
            {expanded ? (
              <ChevronDown className="size-4 text-primary/40" />
            ) : (
              <ChevronRight className="size-4 text-primary/40" />
            )}
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {nameEdit ? (
                <Input
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={commitName}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitName();
                    if (e.key === "Escape") {
                      setNameDraft(group.name);
                      setNameEdit(false);
                    }
                  }}
                  className="h-7 w-64 text-sm font-semibold"
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setNameDraft(group.name);
                    setNameEdit(true);
                  }}
                  className="text-sm font-semibold text-primary transition hover:text-primary/70"
                  title="Click to rename"
                >
                  {group.name}
                </button>
              )}
              <Badge variant="outline" className="font-mono text-[10px]">
                {group.slug}
              </Badge>
            </div>
            <p className="text-xs text-primary/40">
              {group.options.length} option
              {group.options.length === 1 ? "" : "s"} · {snippets.length}{" "}
              variant
              {snippets.length === 1 ? "" : "s"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setShowAddOption(true);
              setExpanded(true);
            }}
            className="gap-1.5"
          >
            <Plus className="size-3.5" />
            Add option
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                className="rounded-md p-1.5 text-destructive/50 transition hover:bg-destructive/10 hover:text-destructive/80"
                title="Delete variant group"
              >
                <Trash2 className="size-3.5" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Delete &ldquo;{group.name}&rdquo;?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Removes the group and every variant inside it. Variant blocks
                  already embedded in templates fall back to their first option
                  at render time.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteGroup}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete group
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {expanded && (
        <div className="divide-y divide-border/30">
          <FieldBindingSection
            group={group}
            customFields={bindingCustomFields}
            classifySource={classifySource}
            onSave={async (binding) => {
              try {
                await updateGroup({
                  groupId: group._id,
                  fieldBinding: binding,
                });
                toast.success("Data trigger saved");
                return true;
              } catch (err) {
                toast.error(
                  extractErrorMessage(err, "Failed to save data trigger"),
                );
                return false;
              }
            }}
            onRemove={async () => {
              try {
                await updateGroup({ groupId: group._id, fieldBinding: null });
                toast.success("Data trigger removed");
              } catch (err) {
                toast.error(
                  extractErrorMessage(err, "Failed to remove data trigger"),
                );
              }
            }}
          />
          {group.options.map((option) => (
            <OptionSection
              key={option.value}
              orgId={orgId}
              group={group}
              option={option}
              snippets={snippets.filter((s) => s.optionValue === option.value)}
              groupSlotLabels={[
                ...new Set(
                  snippets
                    .map((s) => s.slotLabel)
                    .filter((l): l is string => !!l),
                ),
              ]}
              onRename={(next) => renameOption(option.value, next)}
              onDelete={() => deleteOption(option.value)}
              canDelete={group.options.length > 1}
            />
          ))}
          {showAddOption && (
            <div className="bg-secondary/20 px-6 py-3">
              <NewOptionRow
                onCancel={() => setShowAddOption(false)}
                onCreate={addOption}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data Triggers — data-driven variant selection (MOR-886)
// ---------------------------------------------------------------------------

/**
 * The Variants Library is org-level, so a data trigger may target any contact
 * role / firm category the org uses — there's no template whose tags could
 * narrow the list. Offer them all so the picker can surface every
 * `contact_job_*` source field (e.g. `contact_job_security_interest_pmsi`).
 */
const BINDING_CONTACT_TAGS = {
  jobRoles: [...CONTACT_ROLE_KINDS],
  firmCategories: [...FIRM_CATEGORY_ORDER],
};

/** Sentinel for the "no default — leave unselected" Select item. */
const BINDING_NO_DEFAULT = "__no_default__";

/**
 * Inline editor for a variant group's data trigger. Collapsed, it shows a
 * one-line summary ("Auto-selects from <field>"); expanded, it edits the
 * field key, the value → option mappings and the fallback option. Saving
 * with no mappings is blocked — the mutation validates the same rules.
 */
function FieldBindingSection({
  group,
  customFields,
  classifySource,
  onSave,
  onRemove,
}: {
  group: VariantGroup;
  customFields: CustomFieldDef[];
  classifySource: (key: string) => FieldSource;
  onSave: (binding: VariantFieldBinding) => Promise<boolean>;
  onRemove: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldKey, setFieldKey] = useState(group.fieldBinding?.fieldKey ?? "");
  const [mappings, setMappings] = useState<
    Array<{ value: string; optionValue: string }>
  >(group.fieldBinding?.mappings ?? [{ value: "", optionValue: "" }]);
  const [defaultOption, setDefaultOption] = useState(
    group.fieldBinding?.defaultOptionValue ?? BINDING_NO_DEFAULT,
  );

  const beginEdit = useCallback(() => {
    setFieldKey(group.fieldBinding?.fieldKey ?? "");
    setMappings(
      group.fieldBinding?.mappings?.length
        ? group.fieldBinding.mappings
        : [{ value: "", optionValue: group.options[0]?.value ?? "" }],
    );
    setDefaultOption(
      group.fieldBinding?.defaultOptionValue ?? BINDING_NO_DEFAULT,
    );
    setEditing(true);
  }, [group]);

  const optionLabel = useCallback(
    (value: string | undefined) =>
      group.options.find((o) => o.value === value)?.label ?? value ?? "",
    [group.options],
  );

  const submit = useCallback(async () => {
    const trimmedKey = fieldKey.trim();
    const cleanMappings = mappings
      .map((m) => ({ value: m.value.trim(), optionValue: m.optionValue }))
      .filter((m) => m.value && m.optionValue);
    if (!trimmedKey) {
      toast.error("Choose the field the data trigger reads");
      return;
    }
    if (cleanMappings.length === 0) {
      toast.error("Add at least one value → option mapping");
      return;
    }
    setSaving(true);
    try {
      const ok = await onSave({
        fieldKey: trimmedKey,
        mappings: cleanMappings,
        defaultOptionValue:
          defaultOption === BINDING_NO_DEFAULT ? undefined : defaultOption,
      });
      if (ok) setEditing(false);
    } finally {
      setSaving(false);
    }
  }, [fieldKey, mappings, defaultOption, onSave]);

  const binding = group.fieldBinding;

  return (
    <div className="bg-secondary/10 px-6 py-3">
      {!editing ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 text-xs text-primary/60">
            {/*
              Teal, deliberately NOT the indigo used by variant/slot badges —
              the two sat side by side and read as the same kind of thing.
            */}
            <span className="mr-1.5 inline-flex items-center gap-1 rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-700 dark:bg-teal-950/30 dark:text-teal-300">
              {binding ? "Data trigger applied" : "Data trigger"}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="What is a data trigger?"
                    className="inline-flex items-center text-teal-600/70 transition hover:text-teal-800 dark:text-teal-400/70 dark:hover:text-teal-200"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Info className="size-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs text-xs">
                  A data trigger picks this group&apos;s variant automatically
                  from a field&apos;s value on each generated document — for
                  example, use the PMSI wording whenever the security
                  interest&apos;s PMSI field is &ldquo;Yes&rdquo;. Without one,
                  someone chooses the variant by hand on every job. A manual
                  pick in the wizard always overrides the automatic choice.
                </TooltipContent>
              </Tooltip>
            </span>
            {binding ? (
              <>
                Auto-selects from{" "}
                <code className="rounded bg-secondary px-1 py-0.5 font-mono text-[11px]">
                  {binding.fieldKey}
                </code>{" "}
                —{" "}
                {binding.mappings
                  .map((m) => `"${m.value}" → ${optionLabel(m.optionValue)}`)
                  .join(", ")}
                {binding.defaultOptionValue
                  ? `, otherwise ${optionLabel(binding.defaultOptionValue)}`
                  : ""}
              </>
            ) : (
              "None — the variant is selected manually on each job/document."
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button size="sm" variant="outline" onClick={beginEdit}>
              {binding ? "Edit data trigger" : "Add data trigger"}
            </Button>
            {binding && (
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => void onRemove()}
              >
                Remove
              </Button>
            )}
            {/*
              The group header and each option row end in a 26px trash
              button. This row has no delete action, so reserve the same
              slot — otherwise "Add data trigger" overhangs "Add option" and
              "New variant" by exactly that width.
            */}
            <div className="size-[26px] shrink-0" aria-hidden />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-xs text-primary/60">
            Choose the variant automatically per generated document from a field
            value. Manual selections in the wizard still override the automatic
            choice.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-primary/70">Field</span>
            {/*
              Same picker as the PDF field editor and Insert Field menu:
              filter by source first, then search within it, so the author
              picks a labelled field instead of recalling a raw key from a
              flat alphabetical list.
            */}
            <CanonicalKeyPicker
              value={fieldKey}
              onChange={setFieldKey}
              className="h-8 w-96 max-w-full text-xs"
              customFields={customFields}
              contactTags={BINDING_CONTACT_TAGS}
              classifySource={classifySource}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            {mappings.map((m, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-xs text-primary/50">When value is</span>
                <Input
                  value={m.value}
                  onChange={(e) =>
                    setMappings((prev) =>
                      prev.map((row, i) =>
                        i === idx ? { ...row, value: e.target.value } : row,
                      ),
                    )
                  }
                  placeholder="e.g. Yes"
                  className="h-8 w-32 text-xs"
                />
                <span className="text-xs text-primary/50">use</span>
                <Select
                  value={m.optionValue || undefined}
                  onValueChange={(val) =>
                    setMappings((prev) =>
                      prev.map((row, i) =>
                        i === idx ? { ...row, optionValue: val } : row,
                      ),
                    )
                  }
                >
                  <SelectTrigger className="h-8 w-44 text-xs">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    {group.options.map((o) => (
                      <SelectItem
                        key={o.value}
                        value={o.value}
                        className="text-xs"
                      >
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {mappings.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      setMappings((prev) => prev.filter((_, i) => i !== idx))
                    }
                    className="rounded-md p-1 text-primary/40 transition hover:bg-secondary hover:text-primary/70"
                    aria-label="Remove mapping"
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setMappings((prev) => [
                  ...prev,
                  { value: "", optionValue: group.options[0]?.value ?? "" },
                ])
              }
              className="self-start text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              + Add mapping
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-primary/70">
              When no value matches
            </span>
            <Select value={defaultOption} onValueChange={setDefaultOption}>
              <SelectTrigger className="h-8 w-56 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BINDING_NO_DEFAULT} className="text-xs">
                  Leave unselected (manual / job default)
                </SelectItem>
                {group.options.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-xs">
                    Use {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button size="sm" onClick={submit} disabled={saving}>
              {saving && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
              Save data trigger
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Option Row (inline)
// ---------------------------------------------------------------------------

function NewOptionRow({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (label: string) => Promise<void> | void;
}) {
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(async () => {
    if (!label.trim()) return;
    setSubmitting(true);
    try {
      await onCreate(label.trim());
      setLabel("");
    } finally {
      setSubmitting(false);
    }
  }, [label, onCreate]);

  return (
    <div className="flex items-center gap-2 rounded-md border border-dashed border-border/60 bg-background p-2.5">
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void submit();
          }
          if (e.key === "Escape") onCancel();
        }}
        placeholder='New option label (e.g. "Multiple")'
        className="h-8 flex-1 text-sm"
        autoFocus
      />
      <Button
        size="sm"
        onClick={submit}
        disabled={submitting || !label.trim()}
        className="gap-1.5"
      >
        {submitting ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <Plus className="size-3.5" />
        )}
        Add
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Option Section — snippets for one (group, option) pair
// ---------------------------------------------------------------------------

function OptionSection({
  orgId,
  group,
  option,
  snippets,
  groupSlotLabels,
  onRename,
  onDelete,
  canDelete,
}: {
  orgId: Id<"organizations">;
  group: VariantGroup;
  option: { value: string; label: string };
  snippets: VariantSnippet[];
  groupSlotLabels: string[];
  onRename: (nextLabel: string) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
  canDelete: boolean;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<VariantSnippet | null>(null);
  const [labelEdit, setLabelEdit] = useState(false);
  const [labelDraft, setLabelDraft] = useState(option.label);

  const { mutateAsync: createSnippet } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.variantSnippets.createSnippet,
    ),
  });
  const { mutateAsync: deleteSnippet } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.variantSnippets.deleteSnippet,
    ),
  });

  const handleCreate = useCallback(
    async (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      try {
        const id = await createSnippet({
          orgId,
          groupSlug: group.slug,
          optionValue: option.value,
          name: trimmed,
          contentHtml: "<p></p>",
        });
        setShowCreate(false);
        toast.success(`Created "${trimmed}"`);
        // Open the editor for the new snippet so the user can write content
        // immediately.
        setEditing({
          _id: id,
          name: trimmed,
          contentHtml: "<p></p>",
          groupSlug: group.slug,
          optionValue: option.value,
        });
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to create variant"));
      }
    },
    [orgId, group.slug, option.value, createSnippet],
  );

  const handleDeleteSnippet = useCallback(
    async (s: VariantSnippet) => {
      try {
        await deleteSnippet({ snippetId: s._id });
        toast.success(`Deleted "${s.name}"`);
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to delete variant"));
      }
    },
    [deleteSnippet],
  );

  const commitLabel = useCallback(async () => {
    setLabelEdit(false);
    if (labelDraft.trim() === option.label) {
      setLabelDraft(option.label);
      return;
    }
    await onRename(labelDraft);
  }, [labelDraft, option.label, onRename]);

  return (
    <div className="px-6 py-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-baseline gap-2">
          {labelEdit ? (
            <Input
              value={labelDraft}
              onChange={(e) => setLabelDraft(e.target.value)}
              onBlur={commitLabel}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitLabel();
                if (e.key === "Escape") {
                  setLabelDraft(option.label);
                  setLabelEdit(false);
                }
              }}
              className="h-7 w-48 text-sm font-medium"
              autoFocus
            />
          ) : (
            <button
              type="button"
              onClick={() => {
                setLabelDraft(option.label);
                setLabelEdit(true);
              }}
              className="text-sm font-medium text-primary transition hover:text-primary/70"
              title="Click to rename"
            >
              {option.label}
            </button>
          )}
          <span className="font-mono text-[10px] text-primary/30">
            {option.value}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!showCreate && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowCreate(true)}
              className="gap-1.5"
            >
              <Plus className="size-3.5" />
              New variant
            </Button>
          )}
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  type="button"
                  className="rounded-md p-1.5 text-destructive/40 transition hover:bg-destructive/10 hover:text-destructive/70"
                  title="Delete option"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Delete option &ldquo;{option.label}&rdquo;?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Removes this option from the group and orphans any variants
                    tied to it ({snippets.length}{" "}
                    {snippets.length === 1 ? "variant" : "variants"}). Variant
                    blocks already using this option in templates won't have a
                    matching tab anymore.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete option
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {showCreate && (
        <NewSnippetRow
          onCancel={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}

      {snippets.length === 0 && !showCreate && (
        <p className="text-xs italic text-primary/35">
          No variants for this option.
        </p>
      )}

      <div className="space-y-1.5">
        {snippets.map((s) => (
          <SnippetRow
            key={s._id}
            snippet={s}
            onEdit={() => setEditing(s)}
            onDelete={() => handleDeleteSnippet(s)}
          />
        ))}
      </div>

      {editing && (
        <SnippetEditorDialog
          orgId={orgId}
          snippet={editing}
          group={group}
          option={option}
          existingSlotLabels={groupSlotLabels}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Snippet Row (inline name input)
// ---------------------------------------------------------------------------

function NewSnippetRow({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (name: string) => Promise<void> | void;
}) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await onCreate(name.trim());
    } finally {
      setSubmitting(false);
    }
  }, [name, onCreate]);

  return (
    <div className="mb-2 flex items-center gap-2 rounded-md border border-dashed border-border/60 bg-secondary/20 p-2.5">
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void submit();
          }
          if (e.key === "Escape") onCancel();
        }}
        placeholder="Variant name (e.g. Standard opener)"
        className="h-8 flex-1 text-sm"
        autoFocus
      />
      <Button
        size="sm"
        onClick={submit}
        disabled={submitting || !name.trim()}
        className="gap-1.5"
      >
        {submitting ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <Plus className="size-3.5" />
        )}
        Create
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Snippet Row
// ---------------------------------------------------------------------------

function SnippetRow({
  snippet,
  onEdit,
  onDelete,
}: {
  snippet: VariantSnippet;
  onEdit: () => void;
  onDelete: () => void;
}) {
  // Render the snippet body as styled HTML (with field chips) so users see
  // exactly what will appear in the document, not just stripped text.
  const previewHtml = useMemo(() => {
    const raw = snippet.contentHtml?.trim() ?? "";
    if (!raw) return null;
    const chipped = decorateFieldChipModifiers(
      prepareHtmlForEditor(raw, buildFieldsLabelMap([])),
    );
    return DOMPurify.sanitize(chipped, {
      ADD_ATTR: [
        "data-type",
        "data-field-key",
        "data-field-label",
        "data-field-mods",
      ],
    });
  }, [snippet.contentHtml]);

  return (
    <div
      className="group flex items-start justify-between gap-3 rounded-md border border-border/40 bg-background px-3 py-2 transition hover:border-border hover:bg-secondary/20"
      onClick={onEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-primary">{snippet.name}</div>
          {snippet.slotLabel ? (
            <span className="inline-flex items-center rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-600 dark:bg-indigo-950/30 dark:text-indigo-400">
              {snippet.slotLabel}
            </span>
          ) : (
            <span className="text-[10px] italic text-amber-600">no slot</span>
          )}
        </div>
        {previewHtml ? (
          <div
            className="prose-letter snippet-preview mt-1 max-h-24 overflow-hidden text-xs text-primary/70"
            // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- previewHtml is DOMPurify.sanitize'd in the previewHtml memo above
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        ) : (
          <div className="mt-1 text-xs italic text-primary/30">(empty)</div>
        )}
      </div>
      <div
        className="flex items-center gap-1 opacity-0 transition group-hover:opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onEdit}
          className="rounded-md p-1.5 text-primary/40 transition hover:bg-secondary hover:text-primary/70"
          title="Edit variant"
        >
          <Pencil className="size-3.5" />
        </button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              className="rounded-md p-1.5 text-destructive/40 transition hover:bg-destructive/10 hover:text-destructive/80"
              title="Delete variant"
            >
              <Trash2 className="size-3.5" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Delete &ldquo;{snippet.name}&rdquo;?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Templates that previously inserted this variant keep the content
                (it's stored inline). This only removes the entry from the
                library.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Snippet Editor Dialog — full TipTap editor
// ---------------------------------------------------------------------------

function SnippetEditorDialog({
  orgId,
  snippet,
  group,
  option,
  existingSlotLabels,
  onClose,
}: {
  orgId: Id<"organizations">;
  snippet: VariantSnippet;
  group: VariantGroup;
  option: { value: string; label: string };
  existingSlotLabels: string[];
  onClose: () => void;
}) {
  const { mutateAsync: updateSnippet } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.variantSnippets.updateSnippet,
    ),
  });

  const { data: customFieldDefs } = useQuery({
    ...convexQuery(api.settings.precedents.customFields.listByOrg, { orgId }),
  });

  // Pull the firm's branding profile so the variant editor carries through
  // the same colours, fonts and spacing as the full template editor — Oscar
  // couldn't find the firm's navy in the colour picker because these were
  // never passed (MOR-597).
  const { data: brandingProfile } = useQuery({
    ...convexQuery(api.settings.branding.queries.getBrandingProfile, { orgId }),
  });

  const { mutateAsync: createCustomField } = useMutation({
    mutationFn: useConvexMutation(
      api.settings.precedents.customFields.createField,
    ),
  });

  // Inline-renamable name in the dialog header.
  const [name, setName] = useState(snippet.name);
  const [editingName, setEditingName] = useState(false);

  const commitName = useCallback(async () => {
    const trimmed = name.trim();
    setEditingName(false);
    if (!trimmed || trimmed === snippet.name) {
      setName(snippet.name);
      return;
    }
    try {
      await updateSnippet({ snippetId: snippet._id, name: trimmed });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to rename variant"));
      setName(snippet.name);
    }
  }, [name, snippet._id, snippet.name, updateSnippet]);

  // Slot (row) the snippet belongs to. Snippets across options that share a
  // slot label link into one variant — that's what the precedent picker shows.
  const [slot, setSlot] = useState(snippet.slotLabel ?? "");
  const commitSlot = useCallback(async () => {
    const trimmed = slot.trim();
    if (trimmed === (snippet.slotLabel ?? "")) return;
    try {
      await updateSnippet({ snippetId: snippet._id, slotLabel: trimmed });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to set slot"));
      setSlot(snippet.slotLabel ?? "");
    }
  }, [slot, snippet._id, snippet.slotLabel, updateSnippet]);

  // Build the field-picker datasets for the editor — every canonical field
  // and every org custom field is available so the snippet can include any
  // chip a normal template can.
  const fields = useMemo<TemplateEditorField[]>(() => {
    const all: TemplateEditorField[] = [];
    for (const cat of FIELD_CATEGORIES) {
      for (const f of cat.fields) {
        all.push({
          key: f.key,
          label: f.label,
          fieldType: (f.fieldType ?? "text") as string,
        });
      }
    }
    return all;
  }, []);

  const customFields = useMemo<EditorCustomField[]>(() => {
    return (customFieldDefs ?? []).map((d) => ({
      _id: String(d._id),
      categoryId: d.categoryId,
      categoryLabel: d.categoryLabel,
      key: d.key,
      label: d.label,
      fieldType: d.fieldType,
    }));
  }, [customFieldDefs]);

  const handleCreateCustomField = useCallback(
    async (
      input: CreateCustomFieldInput,
    ): Promise<{ key: string; label: string } | null> => {
      try {
        await createCustomField({
          orgId,
          ...input,
        });
        toast.success(`Custom field "${input.label}" created`);
        return {
          key: input.key.trim().toLowerCase().replace(/\s+/g, "_"),
          label: input.label.trim(),
        };
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to create custom field"));
        return null;
      }
    },
    [orgId, createCustomField],
  );

  const handleAutosave = useCallback(
    async (html: string) => {
      try {
        // Drop any highlighter background pasted in from Word / Google Docs so
        // it never bakes into the stored snippet (the source of the green
        // merge-field highlight that leaked into rendered letters and exports).
        await updateSnippet({
          snippetId: snippet._id,
          contentHtml: stripInlineHighlights(html),
        });
      } catch (err) {
        toast.error(extractErrorMessage(err, "Failed to save variant"));
      }
    },
    [snippet._id, updateSnippet],
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[90vh] max-w-5xl flex-col p-0">
        <VisuallyHidden.Root>
          <DialogTitle>Edit variant</DialogTitle>
          <DialogDescription>
            Author the body of a variant using the full editor.
          </DialogDescription>
        </VisuallyHidden.Root>

        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-baseline gap-3">
            {editingName ? (
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={commitName}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitName();
                  if (e.key === "Escape") {
                    setName(snippet.name);
                    setEditingName(false);
                  }
                }}
                className="h-8 w-72 text-sm font-medium"
                autoFocus
              />
            ) : (
              <button
                type="button"
                onClick={() => setEditingName(true)}
                className="text-sm font-semibold text-primary transition hover:text-primary/80"
                title="Click to rename"
              >
                {name}
              </button>
            )}
            <span className="text-xs text-primary/40">
              {group.name} → {option.label}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <label
              htmlFor="snippet-slot-input"
              className="text-xs font-medium text-primary/60"
              title="Snippets across options that share a slot label link into one variant. The precedent picks variants by this label."
            >
              Slot
            </label>
            <Input
              id="snippet-slot-input"
              list="snippet-slot-options"
              value={slot}
              onChange={(e) => setSlot(e.target.value)}
              onBlur={commitSlot}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void commitSlot();
                }
              }}
              placeholder="e.g. Greeting"
              className="h-8 w-48 text-sm"
            />
            <datalist id="snippet-slot-options">
              {existingSlotLabels.map((l) => (
                <option key={l} value={l} />
              ))}
            </datalist>
          </div>
        </div>

        {/* Editor */}
        <div className="min-h-0 flex-1">
          <TemplateEditor
            initialHtml={snippet.contentHtml || "<p></p>"}
            fields={fields}
            customFields={customFields}
            onCreateCustomField={handleCreateCustomField}
            onAutosave={handleAutosave}
            brandingPadding={brandingProfile}
            presetColors={brandingProfile?.editorPresetColors}
            defaultFontFamily={brandingProfile?.editorFontFamily}
            defaultLineHeight={brandingProfile?.editorLineHeight}
            bodyTextColor={brandingProfile?.bodyTextColor}
            defaultFontSize={brandingProfile?.editorFontSize}
            defaultParagraphSpacingBefore={
              brandingProfile?.editorParagraphSpacingBefore
            }
            defaultParagraphSpacingAfter={
              brandingProfile?.editorParagraphSpacingAfter
            }
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Keep TS happy — FieldType is imported but only used to widen string lit.
void (null as unknown as FieldType);
void (null as unknown as CanonicalField);

export default SnippetsSettings;
